import { Section, CopyButton } from "../shared";

const USE_CASES = [
  {
    title: "Check balances & portfolio",
    desc: "Ask your agent about your holdings across all chains.",
    prompts: [
      "What's my wallet balance?",
      "How much ETH do I have on Base?",
      "Show my balance on all chains",
    ],
  },
  {
    title: "Send crypto by name",
    desc: "Send tokens using @usernames or ENS names \u2014 no hex addresses needed.",
    prompts: [
      "Send 0.01 ETH to @akram on Base",
      "Send 20 USDC to vitalik.eth",
      "Transfer 5 USDT to akram.bnb on BSC",
    ],
  },
  {
    title: "Send to emails & phones",
    desc: "Your existing deposit mechanism works through AI agents too.",
    prompts: [
      "Send 10 USDC to ahmed@gmail.com",
      "Send 0.01 ETH to +201234567890",
    ],
  },
  {
    title: "Gas & fee estimates",
    desc: "Check transaction costs before sending.",
    prompts: [
      "How much gas to send ETH on Base?",
      "What's the fee for sending USDC on Polygon?",
      "Estimate gas for sending 100 USDT on Arbitrum",
    ],
  },
  {
    title: "Token prices",
    desc: "Get real-time token prices.",
    prompts: [
      "What's the price of ETH?",
      "How much is BNB worth?",
      "Price of MATIC",
    ],
  },
  {
    title: "Name resolution",
    desc: "Resolve any name across 7 naming services.",
    prompts: [
      "Who is vitalik.eth?",
      "Resolve @akram",
      "Look up akram.bnb",
      "What address is akram.base.eth?",
    ],
  },
  {
    title: "Register @username",
    desc: "Claim your HNS username so anyone can send you crypto by name.",
    prompts: [
      "Register my username as @akram",
      "Set my display name to Ahmed Akram",
      "What's my HNS profile?",
    ],
  },
  {
    title: "Manage wallet config",
    desc: "Control what your AI agent can do \u2014 enable chains, set limits.",
    prompts: [
      "Show my MCP config",
      "Enable BSC chain",
      "Set my daily spending limit to $500",
      "Disable contract write feature",
    ],
  },
  {
    title: "Smart contract interaction",
    desc: "Read and write to smart contracts through conversation.",
    prompts: [
      "Read the totalSupply of USDC on Ethereum",
      "Call balanceOf on USDC for my address",
    ],
  },
  {
    title: "Pay for AI services",
    desc: "Pay for x402 and MPP services automatically.",
    prompts: [
      "How much does this API cost?",
      "Pay for access to https://api.example.com/data",
    ],
  },
  {
    title: "Transaction history",
    desc: "Review your recent activity.",
    prompts: [
      "Show my recent transactions",
      "What did I send last on Base?",
    ],
  },
];

const CONVERSATION = [
  { role: "user", text: "I want to send some USDC to @akram" },
  {
    role: "agent",
    text: "You have USDC on:\n\u2022 Base: 42.5 USDC\n\u2022 Polygon: 120.0 USDC\nWhich chain would you like to use?",
  },
  { role: "user", text: "Base" },
  {
    role: "agent",
    text: "Sending 20 USDC to @akram on Base.\nGas fee: ~$0.003\nProceed?",
  },
  { role: "user", text: "Yes" },
  {
    role: "agent",
    text: "\u2713 Sent 20 USDC to @akram on Base\nTx: 0xabc123...\nView on BaseScan \u2192",
  },
];

const BENEFITS = [
  {
    title: "Natural language",
    desc: "No more copying hex addresses or navigating wallet UIs. Just tell your AI what to do.",
  },
  {
    title: "Safety controls",
    desc: "7-check control engine with spending limits, chain restrictions, and human approval. Your keys are never exposed.",
  },
  {
    title: "Works everywhere",
    desc: "Claude, Copilot, Cursor, Codex, Windsurf \u2014 one API key works with any MCP-compatible agent.",
  },
];

export default function UseCasesSection() {
  return (
    <Section
      title="Use Cases"
      subtitle="What can AI agents do with your wallet?"
      id="mcp-use-cases"
    >
      <p className="mcp-uc-intro">
        Connect any AI agent to your Horus Wallet and let it handle crypto
        tasks through natural conversation.
      </p>

      {/* Category cards */}
      <div className="mcp-uc-grid">
        {USE_CASES.map((uc) => (
          <div key={uc.title} className="mcp-uc-card">
            <h5 className="mcp-uc-card-title">{uc.title}</h5>
            <p className="mcp-uc-card-desc">{uc.desc}</p>
            <div className="mcp-uc-prompts">
              {uc.prompts.map((p) => (
                <div key={p} className="mcp-uc-prompt">
                  <span className="mcp-uc-prompt-text">&ldquo;{p}&rdquo;</span>
                  <CopyButton text={p} label="Copy" className="mcp-btn-xs" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Multi-step conversation */}
      <div className="mcp-uc-conversation-section">
        <h4 className="mcp-subsection-title">Multi-step conversations</h4>
        <div className="mcp-uc-chat">
          {CONVERSATION.map((msg, i) => (
            <div
              key={i}
              className={`mcp-uc-msg ${
                msg.role === "user" ? "mcp-uc-msg-user" : "mcp-uc-msg-agent"
              }`}
            >
              <span className="mcp-uc-msg-role">
                {msg.role === "user" ? "You" : "Agent"}
              </span>
              <span className="mcp-uc-msg-text">{msg.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Why MCP */}
      <div className="mcp-uc-why-section">
        <h4 className="mcp-subsection-title">Why MCP?</h4>
        <div className="mcp-uc-benefits">
          {BENEFITS.map((b) => (
            <div key={b.title} className="mcp-uc-benefit-card">
              <h5 className="mcp-uc-benefit-title">{b.title}</h5>
              <p className="mcp-uc-benefit-desc">{b.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}
